import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChartSkeleton,
  KpiSkeleton,
  WidgetHeaderSkeleton,
} from '@/components/dashboard/primitives/states/WidgetSkeleton';
import { themeCardClass } from '@/components/dashboard/theme/ThemeCard';
import { cn } from '@/lib/utils';

/**
 * The single loading state for the Overview dashboard (`/home`).
 *
 * Opening it used to show three loaders in sequence before the widgets:
 *   1. a bare spinner — the route-level `<Suspense>` fallback in `App.tsx`
 *      while the `DashboardHome` chunk downloads
 *   2. a centred `PageLoadingRabbit` — `DashboardHome` waiting on accounts,
 *      workspaces and onboarding assets
 *   3. each widget's own skeleton, in the real grid
 * Steps 1 and 2 now render this skeleton, which draws the page's own header,
 * KPI row and the top of the widget grid, so the layout appears once and only
 * the widget bodies change when data lands.
 *
 * Built from the widgets' own skeleton primitives and card class, and nothing
 * heavier — it is imported eagerly into `App.tsx`.
 */

// The first rows of the grid in `DASHBOARD_WIDGETS` order (lg column spans).
const GRID_ROWS: ReadonlyArray<ReadonlyArray<string>> = [
  ['lg:col-span-7', 'lg:col-span-5'],
  ['lg:col-span-3', 'lg:col-span-4', 'lg:col-span-5'],
];

const KpiCardSkeleton: React.FC = () => (
  <div className="block w-full rounded-2xl border border-[#E5E7EB] bg-white p-5">
    <Skeleton className="mb-3 h-[13px] w-[88px]" />
    <KpiSkeleton />
  </div>
);

const WidgetCardSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('min-w-0 flex flex-col', className)}>
    <div className={cn('flex flex-1 flex-col', themeCardClass)}>
      <div className="mb-4 shrink-0">
        <WidgetHeaderSkeleton withSubtitle />
      </div>
      <ChartSkeleton height={200} />
    </div>
  </div>
);

export const DashboardPageSkeleton: React.FC = () => (
  <div
    className="min-h-full bg-white font-dash mx-0"
    aria-busy="true"
    aria-label="Loading dashboard"
    data-testid="dashboard-page-skeleton"
  >
    <div className="px-4 sm:px-6 lg:px-10 pt-8 pb-8 max-w-[1280px] w-full mx-auto">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <Skeleton className="h-[32px] w-[240px] rounded-lg" />
        <Skeleton className="h-9 w-[220px] rounded-lg" />
      </header>

      <div className="space-y-5 pb-14">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <KpiCardSkeleton key={i} />
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-12 items-stretch">
          {GRID_ROWS.flat().map((span, i) => (
            <WidgetCardSkeleton key={i} className={span} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default DashboardPageSkeleton;
